import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../styles/admin/institutionDetails.scss";
import {
  getSingleInstitution,
  updateInstitution,
} from "../../helper/apicalls/institution";
import Cookies from "js-cookie";
import GlobalLoader from "../common/GlobalLoader";
import sampleInstitution from "../../assets/png/sampleInstitution.png";

const InstitutionDetails = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const authToken = Cookies.get("admin_token");
  const [institutionDetail, setInstitutionDetail] = useState({});
  const [loader, setLoader] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const detailsRef = useRef(null);
  const [editAdminPerms, setEditAdminPerms] = useState([]);
  const [editTeacherPerms, setEditTeacherPerms] = useState([]);
  const [isDetailsEditing, setIsDetailsEditing] = useState(false);
  const [editDetails, setEditDetails] = useState({
    name: "",
    board: "",
    website_url: "",
    address: "",
    description: "",
  });

  const ADMIN_PERMISSION_TYPES = [
    "course",
    "batches",
    "teacher",
    "student",
    "questionbank",
    "material",
  ];

  const TEACHER_PERMISSION_TYPES = [
    "course",
    "batches",
    "student",
    "questionbank",
    "material",
  ];

  const isValidWebsiteUrl = (value) => {
    try {
      const url = new URL(value);
      return (
        ["http:", "https:"].includes(url.protocol) &&
        url.hostname.includes(".") &&
        !/\s/.test(value)
      );
    } catch {
      return false;
    }
  };

  const validateDetails = () => {
    const newErrors = {};
    const websiteUrl = editDetails.website_url.trim();

    if (!editDetails.name.trim()) {
      newErrors.name = "Institution name is required";
    } else if (editDetails.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (websiteUrl && !isValidWebsiteUrl(websiteUrl)) {
      newErrors.website_url =
        "Please enter a valid website URL starting with http:// or https://";
    }

    setErrors(newErrors);
    const firstErrorKey = ["name", "website_url"].find(
      (key) => newErrors[key],
    );

    if (firstErrorKey) {
      requestAnimationFrame(() => {
        const field = detailsRef.current?.querySelector(
          `[data-error-field="${firstErrorKey}"]`,
        );
        field?.scrollIntoView({ behavior: "smooth", block: "center" });
        field?.querySelector("input, textarea")?.focus({ preventScroll: true });
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleDetailsChange = (field, value) => {
    setEditDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const getInstitutionData = async () => {
    setLoader(true);
    try {
      const res = await getSingleInstitution(authToken, id);
      setInstitutionDetail(res || {});
    } catch (err) {
      console.error("Failed to load institution details:", err);
    } finally {
      setLoader(false);
    }
  };

  const buildPermArrayFromDetail = (listKey, role) => {
    const incoming = Array.isArray(institutionDetail?.[listKey])
      ? institutionDetail[listKey]
      : [];
    const roleData =
      role === "admin"
        ? ADMIN_PERMISSION_TYPES
        : TEACHER_PERMISSION_TYPES || [];

    return roleData?.map((type) => {
      const found = incoming.find((p) => p.permission_type === type);
      return {
        permission_type: type,
        read: !!found?.read,
        write: !!found?.write,
      };
    });
  };

  const startEdit = () => {
    setEditAdminPerms(buildPermArrayFromDetail("admin_permissions", "admin"));
    setEditTeacherPerms(
      buildPermArrayFromDetail("teacher_permissions", "teacher")
    );
    setIsEditing(true);
    setErrors({});
    setEditDetails({
      name: institutionDetail?.name || "",
      board: institutionDetail?.board || "",
      website_url: institutionDetail?.website_url || "",
      address: institutionDetail?.address || "",
      description: institutionDetail?.description || "",
    });
    setIsDetailsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setIsDetailsEditing(false);
    setErrors({});
  };

  const togglePerm = (which, permission_type, key) => {
    if (which === "admin") {
      setEditAdminPerms((prev) =>
        prev.map((p) =>
          p.permission_type === permission_type ? { ...p, [key]: !p[key] } : p
        )
      );
    } else {
      setEditTeacherPerms((prev) =>
        prev.map((p) =>
          p.permission_type === permission_type ? { ...p, [key]: !p[key] } : p
        )
      );
    }
  };

  const handleSave = async () => {
    if (!id) return;
    if (!validateDetails()) return;

    setSaving(true);
    try {
      const payload = {
        name: editDetails.name.trim(),
        board: editDetails.board.trim(),
        website_url: editDetails.website_url.trim(),
        address: editDetails.address.trim(),
        description: editDetails.description.trim(),
        admin_permissions: editAdminPerms.map((p) => ({
          permission_type: p.permission_type,
          read: !!p.read,
          write: !!p.write,
        })),
        teacher_permissions: editTeacherPerms.map((p) => ({
          permission_type: p.permission_type,
          read: !!p.read,
          write: !!p.write,
        })),
      };
      await updateInstitution(payload, authToken, id);
      await getInstitutionData();
      setIsEditing(false);
      setIsDetailsEditing(false);
      setErrors({});
    } catch (e) {
      console.error("Failed to update institution permissions:", e);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    getInstitutionData();
  }, []);

  return (
    <div className="institution-details">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <h4>Institution details</h4>
        {!isEditing ? (
          <button
            className="institution-details-submit-button btn-interactive"
            onClick={startEdit}
            disabled={loader}
          >
            Edit
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="cancel-button institution-details-cancel-button"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="institution-details-submit-button btn-interactive"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>
      {loader && (
        <GlobalLoader visible={loader} text="Loading institution..." />
      )}

      <div className="inst-grid">
        <div className="inst-card inst-section" ref={detailsRef}>
          <div className="inst-section-header-name">
            <h4>{institutionDetail?.name}</h4>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div className="institution-image-container">
              <img
                src={sampleInstitution}
                alt="Institution"
                style={{
                  display: "block",
                  maxWidth: 260,
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 99,
                }}
              />
            </div>
          </div>

          <div className="subsection">
            <div className="desc-box">
              <table
                className={`info-table ${isDetailsEditing ? "isEditable" : ""}`}
              >
                <tbody>
                  <tr
                    className={errors.name ? "has-error" : ""}
                    data-error-field="name"
                  >
                    <td>Name</td>
                    <td>
                      {isDetailsEditing ? (
                        <>
                          <input
                            type="text"
                            value={editDetails.name}
                            onChange={(e) =>
                              handleDetailsChange("name", e.target.value)
                            }
                            placeholder="Institution name"
                            aria-invalid={!!errors.name}
                            aria-describedby={
                              errors.name ? "institution-name-error" : undefined
                            }
                            style={{ width: "100%" }}
                          />
                          {errors.name && (
                            <span
                              id="institution-name-error"
                              className="institution-details-error-message"
                            >
                              {errors.name}
                            </span>
                          )}
                        </>
                      ) : (
                        institutionDetail?.name || "—"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Board</td>
                    <td>
                      {isDetailsEditing ? (
                        <input
                          type="text"
                          value={editDetails.board}
                          onChange={(e) =>
                            handleDetailsChange("board", e.target.value)
                          }
                          placeholder="Board"
                          style={{ width: "100%" }}
                        />
                      ) : (
                        institutionDetail?.board || "—"
                      )}
                    </td>
                  </tr>
                  <tr
                    className={errors.website_url ? "has-error" : ""}
                    data-error-field="website_url"
                  >
                    <td>Website</td>
                    <td>
                      {isDetailsEditing ? (
                        <>
                          <input
                            type="url"
                            value={editDetails.website_url}
                            onChange={(e) =>
                              handleDetailsChange(
                                "website_url",
                                e.target.value,
                              )
                            }
                            placeholder="https://example.com"
                            aria-invalid={!!errors.website_url}
                            aria-describedby={
                              errors.website_url
                                ? "institution-website-error"
                                : undefined
                            }
                            style={{ width: "100%" }}
                          />
                          {errors.website_url && (
                            <span
                              id="institution-website-error"
                              className="institution-details-error-message"
                            >
                              {errors.website_url}
                            </span>
                          )}
                        </>
                      ) : institutionDetail?.website_url ? (
                        <a
                          href={
                            /^https?:\/\//i.test(institutionDetail.website_url)
                              ? institutionDetail.website_url
                              : `https://${institutionDetail.website_url}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {institutionDetail.website_url}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Address</td>
                    <td>
                      {isDetailsEditing ? (
                        <input
                          type="text"
                          value={editDetails.address}
                          onChange={(e) =>
                            handleDetailsChange("address", e.target.value)
                          }
                          placeholder="Address"
                          style={{ width: "100%" }}
                        />
                      ) : (
                        institutionDetail?.address || "—"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="subsection">
            <h4 className="subsection-title">Description</h4>
            {isDetailsEditing ? (
              <textarea
                value={editDetails.description}
                onChange={(e) =>
                  handleDetailsChange("description", e.target.value)
                }
                placeholder="Short description"
                rows={4}
                style={{ width: "100%" }}
              />
            ) : (
              <div className="sub-section-p">
                {institutionDetail?.description || ""}
              </div>
            )}
          </div>
        </div>

        <div className="inst-card inst-section">
          <div
            className="inst-section-header-name"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <h4>Permissions</h4>
          </div>
          <div className="kv-list">
            <div className="kv-key">Admin</div>
            <div className="kv-val">
              <div className="inst-permissions-matrix inst-permissions-matrix--admin">
                <div className="inst-matrix-header">
                  <span className="inst-matrix-col inst-type-col inst-matrix-header-info">
                    Module
                  </span>
                  <span className="inst-matrix-col inst-matrix-header-info text-align">
                    View
                  </span>
                  <span className="inst-matrix-col inst-matrix-header-info text-align">
                    Edit
                  </span>
                </div>
                {ADMIN_PERMISSION_TYPES.map((type) => {
                  const entry = !isEditing
                    ? Array.isArray(institutionDetail?.admin_permissions)
                      ? institutionDetail.admin_permissions.find(
                          (p) => p.permission_type === type
                        )
                      : null
                    : editAdminPerms.find((p) => p.permission_type === type);
                  return (
                    <div key={`admin-${type}`} className="inst-matrix-row">
                      <span className="inst-matrix-col inst-type-col">
                        {type}
                      </span>
                      <span className="inst-matrix-col">
                        <input
                          type="checkbox"
                          checked={!!entry?.read}
                          disabled={!isEditing}
                          onChange={() =>
                            isEditing && togglePerm("admin", type, "read")
                          }
                        />
                      </span>
                      <span className="inst-matrix-col">
                        <input
                          type="checkbox"
                          checked={!!entry?.write}
                          disabled={!isEditing}
                          onChange={() =>
                            isEditing && togglePerm("admin", type, "write")
                          }
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="kv-key">Teacher</div>
            <div className="kv-val">
              <div className="inst-permissions-matrix inst-permissions-matrix--teacher">
                <div className="inst-matrix-header">
                  <span className="inst-matrix-col inst-type-col inst-matrix-header-info">
                    Module
                  </span>
                  <span className="inst-matrix-col inst-matrix-header-info text-align">
                    View
                  </span>
                  <span className="inst-matrix-col inst-matrix-header-info text-align">
                    Edit
                  </span>
                </div>
                {TEACHER_PERMISSION_TYPES.map((type) => {
                  const entry = !isEditing
                    ? Array.isArray(institutionDetail?.teacher_permissions)
                      ? institutionDetail.teacher_permissions.find(
                          (p) => p.permission_type === type
                        )
                      : null
                    : editTeacherPerms.find((p) => p.permission_type === type);
                  return (
                    <div key={`teacher-${type}`} className="inst-matrix-row">
                      <span className="inst-matrix-col inst-type-col">
                        {type}
                      </span>
                      <span className="inst-matrix-col">
                        <input
                          type="checkbox"
                          checked={!!entry?.read}
                          disabled={!isEditing}
                          onChange={() =>
                            isEditing && togglePerm("teacher", type, "read")
                          }
                        />
                      </span>
                      <span className="inst-matrix-col">
                        <input
                          type="checkbox"
                          checked={!!entry?.write}
                          disabled={!isEditing}
                          onChange={() =>
                            isEditing && togglePerm("teacher", type, "write")
                          }
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstitutionDetails;
